import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface Project {
  id: string
  name: string
  description?: string
  image?: string
  createdAt?: string
  updatedAt?: string
}

interface ProjectContextType {
  projects: Project[]
  loading: boolean
  error: string | null
  selectedProject: Project | null
  setSelectedProject: (project: Project | null) => void
  // Aliases used by pages
  currentProject: Project | null
  selectProject: (project: Project | string | null) => void
  loadProjects: () => Promise<void>
  createProject: (name: string, description?: string) => Promise<boolean>
  deleteProject: (projectId: string) => Promise<boolean>
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined)

export const useProjects = () => {
  const context = useContext(ProjectContext)
  if (context === undefined) {
    throw new Error('useProjects must be used within a ProjectProvider')
  }
  return context
}

interface ProjectProviderProps {
  children: ReactNode
}

export const ProjectProvider = ({ children }: ProjectProviderProps) => {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)

  const loadProjects = async () => {
    setLoading(true)
    setError(null)
    try {
      // Resolve username from localStorage (AuthContext persists it there)
      const savedUser = localStorage.getItem('user')
      const username = savedUser ? (JSON.parse(savedUser).username as string) : ''

      if (!username) {
        // Fallback to default project when not logged in
        setProjects([{ id: '1', name: 'Default', createdAt: new Date().toISOString() }])
        return
      }

      const resp = await fetch(`/api/list_projects?username=${encodeURIComponent(username)}`, { credentials: 'include' })
      const data = await resp.json()
      if (!resp.ok || data.status !== 'success') {
        throw new Error(data?.message || 'API error')
      }
      const arr = Array.isArray(data.projects) ? data.projects : []
      const mapped: Project[] = arr.map((p: any, idx: number) => ({
        id: String(idx + 1),
        name: p?.name || 'Unnamed',
        image: p?.thumbnail,
        createdAt: new Date().toISOString()
      }))
      setProjects(mapped)
    } catch (err) {
      setError('Failed to load projects')
      console.error('Error loading projects:', err)
    } finally {
      setLoading(false)
    }
  }

  const createProject = async (name: string, description?: string): Promise<boolean> => {
    try {
      // Resolve username from localStorage (AuthContext persists it there)
      const savedUser = localStorage.getItem('user')
      const username = savedUser ? (JSON.parse(savedUser).username as string) : ''
      if (!username) {
        setError('Nie zalogowano użytkownika')
        return false
      }

      const resp = await fetch('/api/create_project', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, project: name })
      })
      const data = await resp.json()
      if (resp.ok && data.status === 'success') {
        const newName = data.project || name
        const newProject: Project = {
          id: Date.now().toString(),
          name: newName,
          description,
          createdAt: new Date().toISOString()
        }
        setProjects(prev => [...prev, newProject])
        return true
      }
      setError(data?.message || 'Failed to create project')
      return false
    } catch (err) {
      setError('Failed to create project')
      console.error('Error creating project:', err)
      return false
    }
  }

  const deleteProject = async (projectId: string): Promise<boolean> => {
    try {
      setProjects(prev => prev.filter(p => p.id !== projectId))
      if (selectedProject && selectedProject.id === projectId) {
        setSelectedProject(null)
      }
      return true
    } catch (err) {
      setError('Failed to delete project')
      console.error('Error deleting project:', err)
      return false
    }
  }

  useEffect(() => {
    loadProjects()
  }, [])

  const selectProject = (project: Project | string | null) => {
    if (!project) { setSelectedProject(null); return }
    if (typeof project === 'string') {
      const found = projects.find(p => p.name === project || p.id === project)
      setSelectedProject(found || { id: project, name: project })
    } else {
      setSelectedProject(project)
    }
  }

  const value = {
    projects,
    loading,
    error,
    selectedProject,
    setSelectedProject,
    currentProject: selectedProject,
    selectProject,
    loadProjects,
    createProject,
    deleteProject
  }

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  )
}
